import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, KeyRound, Mail, Power } from 'lucide-react';
import {
  getAdminUserOptions,
  getAdminUserQueryKey,
  listAdminUsersQueryKey,
  resetUserPasswordMutation,
  updateAdminUserMutation,
  verifyUserMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, ConfirmDialog, QueryBoundary, useToast } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/DetailPage.js';
import { Can } from '../../components/auth/Can.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../help/index.js';
import { UserProfileTab } from './UserProfileTab.js';
import { UserRolesTab } from './UserRolesTab.js';
import { UserActivityTab } from './UserActivityTab.js';
import { USER_STATUS_LABEL, USER_STATUS_VARIANT, userDisplayName } from './user-labels.js';

const TAB_IDS = ['profile', 'roles', 'activity'] as const;
const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'roles', label: 'Roles and organization' },
  { id: 'activity', label: 'Activity' },
];

export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [tab, setTab] = useTabParam(TAB_IDS, 'profile');
  const [confirm, setConfirm] = useState<'reset-password' | 'toggle-active' | null>(null);

  const query = useQuery({ ...getAdminUserOptions({ path: { id: userId } }), enabled: !Number.isNaN(userId) });
  const user = query.data;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getAdminUserQueryKey({ path: { id: userId } }) }),
      queryClient.invalidateQueries({ queryKey: listAdminUsersQueryKey() }),
    ]);

  const resetPassword = useMutation({
    ...resetUserPasswordMutation(),
    onSuccess: () => {
      setConfirm(null);
      notify('Password reset email sent');
    },
    onError: () => notify('Could not send the password reset email', 'danger'),
  });
  const verify = useMutation({
    ...verifyUserMutation(),
    onSuccess: async () => {
      notify('Verification email sent');
      await refresh();
    },
    onError: () => notify('Could not send the verification email', 'danger'),
  });
  const toggleActive = useMutation({
    ...updateAdminUserMutation(),
    onSuccess: async () => {
      setConfirm(null);
      notify(user?.active ? 'Account deactivated' : 'Account activated');
      await refresh();
    },
    onError: () => notify('Could not update the account', 'danger'),
  });

  const actions = user && (
    <Can policies={['CanManageUsers', 'CanManageOrgMembers']} mode="any">
      {!user.verified && (
        <Button
          variant="secondary"
          size="sm"
          loading={verify.isPending}
          onClick={() => verify.mutate({ path: { id: user.id } })}
        >
          <Mail size={14} aria-hidden="true" />
          Send verification email
        </Button>
      )}
      <Button variant="secondary" size="sm" onClick={() => setConfirm('reset-password')}>
        <KeyRound size={14} aria-hidden="true" />
        Reset password
      </Button>
      <Button variant={user.active ? 'danger' : 'primary'} size="sm" onClick={() => setConfirm('toggle-active')}>
        <Power size={14} aria-hidden="true" />
        {user.active ? 'Deactivate account' : 'Activate account'}
      </Button>
    </Can>
  );

  return (
    <DetailPage
      title={user ? userDisplayName(user) : 'User'}
      subtitle={user ? [user.email, user.jobTitle, user.department].filter(Boolean).join(' · ') : undefined}
      breadcrumbs={[{ label: 'Users', to: '/organization/users' }, { label: user ? userDisplayName(user) : 'User' }]}
      helpContent={PAGE_HELP.users}
      badges={
        user && (
          <>
            <Badge variant={USER_STATUS_VARIANT[user.status]} dot>
              {USER_STATUS_LABEL[user.status]}
            </Badge>
            {!user.active && <Badge variant="neutral">Deactivated</Badge>}
            {user.verified ? (
              <Badge variant="success" icon={<CheckCircle size={12} />}>
                Email verified
              </Badge>
            ) : (
              <Badge variant="warning">Email not verified</Badge>
            )}
          </>
        )
      }
      actions={actions}
      tabs={user ? TABS : undefined}
      activeTab={tab}
      onTabChange={setTab}
    >
      <QueryBoundary query={query} label="user">
        {(loaded) => (
          <>
            {tab === 'profile' && <UserProfileTab key={loaded.updatedAt} user={loaded} />}
            {tab === 'roles' && <UserRolesTab user={loaded} />}
            {tab === 'activity' && <UserActivityTab userId={loaded.id} />}
            <ConfirmDialog
              open={confirm === 'reset-password'}
              onClose={() => setConfirm(null)}
              onConfirm={() => resetPassword.mutate({ path: { id: loaded.id } })}
              title="Reset password"
              message={`Send a password reset email to ${loaded.email}?`}
              confirmLabel="Send email"
              tone="primary"
              loading={resetPassword.isPending}
            />
            <ConfirmDialog
              open={confirm === 'toggle-active'}
              onClose={() => setConfirm(null)}
              onConfirm={() => toggleActive.mutate({ path: { id: loaded.id }, body: { active: !loaded.active } })}
              title={loaded.active ? 'Deactivate account' : 'Activate account'}
              message={
                loaded.active
                  ? `${userDisplayName(loaded)} will no longer be able to sign in. Their history is kept and you can switch the account back on.`
                  : `${userDisplayName(loaded)} will be able to sign in again.`
              }
              confirmLabel={loaded.active ? 'Deactivate' : 'Activate'}
              tone={loaded.active ? 'danger' : 'primary'}
              loading={toggleActive.isPending}
            />
          </>
        )}
      </QueryBoundary>
    </DetailPage>
  );
};
